// ============================================================================
// GROUP CONTAINER - Moldura Visual para Grupos Expandidos
// ============================================================================
//
// Este componente renderiza uma moldura/container ao redor dos cards filhos
// quando um grupo está expandido. Substitui visualmente o Card Roxo.
//
// Características:
// - Background transparente (não bloqueia interações)
// - Borda tracejada roxa para delimitar o grupo
// - Z-Index baixo (fica atrás dos cards filhos)
// - Header com nome do grupo e botão de minimizar
//
// ============================================================================

import React, { useMemo } from 'react';
import { Minimize2, Package } from 'lucide-react';
import { CardData } from '../types';

interface GroupContainerProps {
  /** ID do sub-processo pai */
  groupId: string;
  /** Nome do grupo */
  groupName: string;
  /** Cards filhos (para calcular bounding box) */
  childCards: CardData[];
  /** Callback para minimizar/recolher o grupo */
  onMinimize: () => void;
  /** Escala do canvas */
  scale?: number;
  /** Cor do tema do grupo */
  themeColor?: string;
}

// Padding ao redor dos cards filhos (respiro visual)
const CONTAINER_PADDING = 40;
const HEADER_HEIGHT = 36;

export const GroupContainer: React.FC<GroupContainerProps> = ({
  groupId,
  groupName,
  childCards,
  onMinimize,
  scale = 1,
  themeColor = '#6366f1', // Indigo-500
}) => {
  // 🔒 SEGURANÇA: Filtrar cards marcadores (não devem fazer parte do grupo)
  const MARKER_TYPES = ['card_inicial', 'card_final'];
  const validChildCards = childCards.filter(c => !MARKER_TYPES.includes(c.tipo));
  
  // Calcular bounding box que engloba apenas os cards válidos do grupo
  const bounds = useMemo(() => {
    if (validChildCards.length === 0) {
      return { x: 0, y: 0, width: 300, height: 200 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    validChildCards.forEach(card => {
      const cardWidth = card.tipo === 'decisao' ? 160 : 240;
      const cardHeight = card.tipo === 'decisao' ? 160 : 180;
      
      minX = Math.min(minX, card.posicao.x);
      minY = Math.min(minY, card.posicao.y);
      maxX = Math.max(maxX, card.posicao.x + cardWidth);
      maxY = Math.max(maxY, card.posicao.y + cardHeight);
    });

    return {
      x: minX - CONTAINER_PADDING,
      y: minY - CONTAINER_PADDING - HEADER_HEIGHT,
      width: maxX - minX + CONTAINER_PADDING * 2,
      height: maxY - minY + CONTAINER_PADDING * 2 + HEADER_HEIGHT,
    };
  }, [validChildCards]);

  return (
    <div
      className="group-container absolute pointer-events-none"
      style={{
        left: bounds.x,
        top: bounds.y,
        width: bounds.width,
        height: bounds.height,
        zIndex: 5, // Atrás dos cards (cards são z-30+)
      }}
    >
      {/* Borda do Container */}
      <div
        className="absolute inset-0 rounded"
        style={{
          border: `2px dashed ${themeColor}60`,
          backgroundColor: `${themeColor}08`,
          borderRadius: 16,
        }}
      />

      {/* Header do Container */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-3 pointer-events-auto"
        style={{
          height: HEADER_HEIGHT,
          backgroundColor: `${themeColor}15`,
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          borderBottom: `1px solid ${themeColor}30`,
        }}
      >
        {/* Nome do Grupo */}
        <div className="flex items-center gap-2">
          <Package size={14} style={{ color: themeColor }} strokeWidth={2.5} />
          <span
            className="text-xs font-semibold truncate max-w-[200px]"
            style={{ color: themeColor }}
          >
            {groupName}
          </span>
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-sm font-medium"
            style={{ 
              backgroundColor: `${themeColor}20`,
              color: themeColor,
            }}
          >
            {validChildCards.length} cards
          </span>
        </div>

        {/* Botão Minimizar */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMinimize();
          }}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all duration-150 hover:scale-105"
          style={{
            backgroundColor: `${themeColor}20`,
            color: themeColor,
          }}
          title="Minimizar grupo"
        >
          <Minimize2 size={12} strokeWidth={2.5} />
          <span>Minimizar</span>
        </button>
      </div>

      {/* Indicadores de Canto (visual sutil) */}
      <div
        className="absolute bottom-2 left-2 w-3 h-3 rounded-sm"
        style={{ backgroundColor: `${themeColor}30` }}
      />
      <div
        className="absolute bottom-2 right-2 w-3 h-3 rounded-sm"
        style={{ backgroundColor: `${themeColor}30` }}
      />
    </div>
  );
};

export default GroupContainer;
